/** The dashboard's English text, typed against the Dutch source. */

import type { nl } from "./nl";
import type { Messages } from "./types";

export const en: Messages<typeof nl> = {
  common: {
    loading: "Loading",
    loadingEllipsis: "Loading…",
    tryAgain: "Try again",
    cancel: "Cancel",
    close: "Close",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    actionsFor: "Actions for {{name}}",
    on: "On",
    off: "Off",
    never: "Never",
    pageRange: "{{from, number}}–{{to, number}} of {{total, number}}",
    resetFilters: "Clear filters",
  },
  toast: {
    region: "Notifications",
  },
  language: {
    label: "Language",
  },
  nav: {
    main: "Main",
    connector: "Connector",
    apps: "Apps",
    home: "Home",
    analytics: "Analytics",
    tools: "Tools",
    docs: "Docs",
    settings: "Settings",
    personal: "Personal",
    organization: "Organization",
    developer: "Developers",
    general: "General",
    account: "Account",
    people: "People and teams",
    roadmap: "Roadmap",
    activity: "Activity log",
    about: "About",
    viewAs: "View as",
  },
  shell: {
    skipToContent: "Skip to content",
    openMenu: "Open the menu",
    menu: "Menu",
    menuDescription: "The dashboard's pages, the docs and your account.",
    homeLink: "{{app}} home",
    builtBy: "Built by",
    veltaNewTab: "Velta (opens in a new tab)",
    collapseMenu: "Collapse menu",
    expandMenu: "Expand menu",
  },
  environment: {
    readOnly: "Read-only",
    title: "The {{name}} environment, separate from production.",
    titleReadOnly:
      "The {{name}} environment, separate from production: it reads De Ceuster's live data but never changes anything in Odoo or any other system.",
  },
  accountMenu: {
    fallbackName: "Account",
    signOut: "Sign out",
    persona: "Persona (local)",
    personaNote: "Only in a local test setup",
  },
  session: {
    reconnecting: "Reconnecting…",
    serverDown: "The server is not responding. This page will continue on its own once it is back.",
  },
  errorPage: {
    title: "Something went wrong",
    body: "Reload the page to try again.",
    reload: "Reload",
  },
  notFound: {
    title: "Page not found",
    home: "Go home",
  },
  connector: {
    microsoftDetail: "Sign in with your De Ceuster Microsoft account",
    localLabel: "Local test",
    localDetail: "Without sign-in, on this computer only",
    offDetail: "The connector is not switched on yet",
    odooOnDetail: "Claude reads De Ceuster's Odoo, read-only",
    odooConnectorOffDetail: "While the connector is off",
    odooNotConnectedDetail: "Until Odoo is connected",
  },
  usage: {
    lastDays_one: "Today",
    lastDays_other: "Last {{count}} days",
    noCalls: "No connector calls in the last {{count}} days.",
    calls: "Calls",
    activeUsers: "Active users",
    errors: "Errors",
  },
  chart: {
    successful: "Successful",
    errors: "Errors",
    calls_one: "{{count, number}} call",
    calls_other: "{{count, number}} calls",
    errorCount_one: "{{count, number}} error",
    errorCount_other: "{{count, number}} errors",
    summary: "Calls per day over {{days}} days: {{calls}}, {{errors}}.",
    dayDescription: "{{day}}: {{calls}}, {{errors}}",
    noCallsDay: "{{day}}: no calls",
    errorDayDescription: "{{day}}: {{errors}} of {{calls}} ({{share}})",
    measure: {
      label: "Show as",
      count: "Count",
      percent: "Percentage",
    },
  },
  table: {
    sortedAscending: "sorted ascending",
    sortedDescending: "sorted descending",
  },
  filters: {
    search: "Search",
    empty: "Nothing found.",
    clear: "Clear",
    toolCount_one: "{{count, number}} tool",
    toolCount_other: "{{count, number}} tools",
    personCount_one: "{{count, number}} person",
    personCount_other: "{{count, number}} people",
    actionCount_one: "{{count, number}} action",
    actionCount_other: "{{count, number}} actions",
  },
  period: {
    label: "Period",
    last: {
      week: "Last week",
      month: "Last month",
      quarter: "Last quarter",
    },
  },
  timeRange: {
    label: "Time range",
    value: "Time range: {{range}}",
    custom: "Custom",
    range: "{{from}} to {{to}}",
    from: "From",
    to: "To",
    startDay: "Start day",
    endDay: "End day",
    startTime: "Start time",
    endTime: "End time",
    chooseDay: "Choose a day",
    apply: "Apply",
    belgianTime: "In Belgian time. The end includes that whole minute.",
    problem: {
      day: "Choose a day for the start and for the end.",
      time: "Type a time such as 08:30, between 00:00 and 23:59.",
      order: "The end must come after the start.",
      tooLong: "Choose a range of at most a year.",
    },
  },
  dayPicker: {
    value: "{{label}}: {{day}}",
    previousMonth: "Previous month",
    nextMonth: "Next month",
    clear: "Clear",
  },
  home: {
    morning: "Good morning",
    afternoon: "Good afternoon",
    evening: "Good evening",
    status: "Status",
    odooTools: "Odoo tools",
    statusLoadError: "Could not load the connector's status.",
    usageLoadError: "Could not load the connector's use.",
    docsTitle: "How to use the connector in Claude",
    docsBody:
      "Connect Claude once with your De Ceuster Microsoft account, then ask about clients, cranes, orders and invoices in plain words. The docs explain how, what you can ask, and what the connector keeps.",
    docsLink: "Read the docs",
    issues: {
      title: "Failures to analyse",
      failedLastWeek_one: "failed {{count, number}} time in the last 7 days",
      failedLastWeek_other: "failed {{count, number}} times in the last 7 days",
      lastFailed: "last failed on {{time}}",
      more_one: "{{count, number}} more issue to analyse",
      more_other: "{{count, number}} more issues to analyse",
      acknowledged_one: "{{count, number}} in progress at Velta",
      acknowledged_other: "{{count, number}} in progress at Velta",
    },
  },
  tools: {
    retired: {
      label: "No longer available",
      hint: "DC Data no longer offers this tool. Its earlier calls stay on record and visible.",
    },
    description: "The tools Claude can use for you through DC Data.",
    loadError: "Could not load the tools.",
    switchesUnknown: "The tool switches could not be read just now. Try again in a minute.",
    access: {
      read: "Read",
      write: "Write",
    },
    accessLetter: {
      read: "R",
      write: "W",
    },
    comingSoon: "Coming soon",
    paused: "Paused",
    personalSwitchLabel: "Let Claude use {{title}} for me",
    alwaysOn: "Always on",
    unavailable: "Not available",
    unavailableReason: {
      connectorOff: "The connector is off",
      needsSignIn: "Only with Microsoft sign-in",
      writesOff: "Read-only in this environment",
      odooNotConnected: "Odoo is not connected",
      appOff: "The {{app}} app is off",
    },
    column: {
      tool: "Tool",
      audience: "For",
      personal: "For me",
    },
    audience: {
      everyone: "Everyone",
      team: "Per team",
      admin: "Admins",
      owner: "Owner",
      developer: "Developers",
    },
    audienceShort: {
      developer: "Dev.",
    },
    audienceHint: {
      everyone: "Every colleague",
      team: "Anyone in a team that gets this group, see People and teams",
      admin: "Admins, the owner and the developers",
      owner: "The owner and the developers",
      developer: "Developers only",
    },
    groupCount: {
      tools_one: "{{count, number}} tool",
      tools_other: "{{count, number}} tools",
      read: "{{count, number}} read",
      write: "{{count, number}} write",
      soon: "{{count, number}} coming soon",
    },
    kind: {
      odoo: "Odoo",
      account: "Your account",
      submission: "Ideas and questions",
      role: "For your role",
    },
    filter: {
      search: "Search by name or description",
      allAccess: "All access",
      kind: "Kind",
      allKinds: "All kinds",
      group: "Group",
      allGroups: "All groups",
      groupCount_one: "{{count}} group",
      groupCount_other: "{{count}} groups",
      empty: "No tools found.",
    },
    details: {
      access: "Access",
      group: "Group",
      audience: "For",
      size: "Size",
      sizeValue: "about {{tokens}} tokens",
      usage: "Usage",
      usageLink: "Calls in Analytics",
      comingSoon: "Claude cannot use this tool yet.",
      notServed: "Claude does not get this tool here.",
      notServedBecause: "Claude does not get this tool here. {{reason}}.",
      claudeReads: "What Claude reads",
      noDescription: "Claude gets no description of this tool.",
      parameters: "Parameters",
      noParameters: "This tool has no parameters.",
      parameter: {
        name: "Parameter",
        type: "Type",
        required: "Required",
        default: "Default",
      },
      yes: "Yes",
      no: "No",
      requiredFact: "required",
      defaultFact: "default {{value}}",
      loading: "Loading the description for Claude…",
      killSwitch: "Off for everyone",
      killSwitchHint: "While this is on, nobody gets this tool in Claude.",
    },
  },
  analytics: {
    description:
      "Use of the MCP connector DC Data in Claude: metadata only, never what was asked or answered.",
    tabs: {
      overview: "Overview",
      calls: "Calls",
      failures: "Failures",
      speed: "Speed",
    },
    loadError: "Failed to load connector usage.",
    stale: "The connector data could not be refreshed, so you see what was loaded last.",
    failedCalls: "Failed calls",
    shareOfCalls: "{{share}} of the calls",
    callsPerDay: "Calls per day",
    byUser: "Per person",
    byTool: "Per tool",
    tables: "Tables",
    usersEmpty: "Nobody used the connector in this period.",
    usersSearch: "Search by name or email",
    usersNoMatch: "Nobody found.",
    user: "User",
    person: "Person",
    people: "People",
    everyone: "Everyone",
    searchPerson: "Search for a person",
    errors: "Errors",
    lastCall: "Last call",
    lastCallFact: "last call {{time}}",
    status: "Status",
    onlyWithErrors: "Only with errors",
    blocked: "Blocked",
    blockedHint:
      "Velta has taken this person's access to DC Data away. You block and unblock people under People and teams, on the Teams tab.",
    withoutSignIn: "Without sign-in",
    allTools: "All tools",
    searchTool: "Search for a tool",
    outcome: {
      label: "Outcome",
      all: "All outcomes",
      ok: "Succeeded",
      failed: "Failed",
    },
    time: "Time",
    tool: "Tool",
    duration: "Duration",
    callsLoadError: "Failed to load the calls.",
    callsEmpty: "No calls found.",
    ok: "OK",
    error: "Error",
    previousPage: "Previous",
    nextPage: "Next",
    toolsEmpty: "No tool calls in this period.",
    calls: "Calls",
    callsColumn: "To the calls",
    callsOf: "View the calls of {{name}}",
    meanDuration: "Average duration",
    meanFact: "average {{duration}}",
    failuresFact: "{{failures}} failed",
    detail: {
      column: "Details",
      toggle: "Details of the call of {{time}}",
      failure: "Failed:",
      toIssue: "To the issue",
      noOdoo:
        "This call did not go to Odoo, or it is older than 1 November 2026, when DC Data started keeping Odoo's figures.",
      figures: {
        odoo_requests: "Requests to Odoo",
        odoo_ms: "Time in Odoo",
        odoo_max_ms: "Slowest request",
        worker_wait_ms: "Waiting for a worker",
        spacing_wait_ms: "Waiting for its turn",
        retry_wait_ms: "Waiting for a new attempt",
        retries: "New attempts",
        rate_limits: "Times slowed down by Odoo",
        odoo_status: "Odoo's last answer",
      },
    },
  },
  issues: {
    loadError: "Failed to load the connector's failures.",
    perDay: "Failed calls per day",
    noFailures: "No failed calls in the last {{count}} days.",
    wholeConnector: "Whole connector",
    roadmapRule: "Not allowed by the roadmap's rules",
    status: {
      new: {
        label: "New",
        hint: "Velta has not looked at it yet.",
      },
      regressed: {
        label: "Came back",
        hint: "Velta had resolved this, but it has failed again since.",
      },
      open: {
        label: "Open",
        hint: "Opened again. Velta still has to analyse it.",
      },
      acknowledged: {
        label: "In progress",
        hint: "Velta is analysing this issue. It stays in progress, also when it fails again.",
      },
      resolved: {
        label: "Resolved",
        hint: "Velta has resolved this. If it fails again, it comes back as Came back.",
      },
      ignored: {
        label: "Ignored",
        hint: "Velta leaves this as it is on purpose, for example because the connector rightly refused.",
      },
    },
    category: {
      refused: "Refused by DC Data",
      request: "Question it cannot answer",
      odoo: "Odoo error",
      timeout: "Time-out",
      cancelled: "Cancelled",
      internal: "Internal error",
    },
    kinds: {
      user_blocked: "Person blocked",
      user_unidentified: "Person not recognised",
      account_not_allowed: "Not a De Ceuster account",
      tool_switched_off: "Tool switched off",
      tool_switched_off_by_user: "Tool switched off by the person",
      tool_not_in_team: "Tool not in the team",
      tool_not_for_role: "Tool not for this role",
      not_allowed: "Not allowed",
      roadmap_forbidden: "Not allowed for this role",
      tool_not_found: "Unknown tool",
      unknown_tool: "Unknown tool",
      invalid_arguments: "Invalid question",
      not_found: "Not found",
      refused: "Refused by the tool",
      result_too_large: "Answer too large",
      too_many_rows: "Too many results",
      triage_entry_not_found: "Not found in the triage",
      triage_entry_closed: "Already closed in the triage",
      odoo_unavailable: "Odoo unreachable",
      odoo_error: "Error in Odoo",
      access_denied: "Refused by Odoo",
      odoo_access_refused: "Odoo refuses access",
      odoo_data_missing: "Missing in Odoo",
      odoo_field_missing: "Field missing in Odoo",
      odoo_slow: "Odoo too slow",
      odoo_busy: "Odoo busy",
      odoo_time_budget: "Time limit reached",
      cancelled: "Cancelled by Claude",
      compute_failed: "Computation failed",
      unavailable: "Not available",
      access_check_unavailable: "Access could not be checked",
      tool_switches_unavailable: "Switches not readable",
      team_access_unavailable: "Teams not readable",
      role_unavailable: "Role not readable",
      ErrorResult: "Error in the tool's answer",
    },
    list: {
      title: "Issues",
      label: "Issues",
      search: "Search by issue or tool",
      category: "Kind",
      allStatuses: "All statuses",
      allCategories: "All kinds",
      noMatch: "No issues found.",
      lastSeenFact: "last seen {{time}}",
      usersFact_one: "{{count, number}} person",
      usersFact_other: "{{count, number}} people",
    },
    columns: {
      problem: "Issue",
      status: "Status",
      count: "Times",
      users: "People",
      lastSeen: "Last seen",
      firstSeen: "First seen",
      perDay: "Per day",
    },
    page: {
      back: "All failures",
      loadError: "Failed to load this issue.",
      notFound:
        "This issue does not exist, or its failed calls are no longer kept. DC Data keeps them for 12 months.",
      statusSince: "status since {{time}}",
      regressedSince: "resolved on {{time}} and failed again since",
      calls: "Failed calls",
      noFailures: "No failed calls in the last {{count}} days.",
      facts: {
        inPeriod: "Failed in this period",
        shareOfToolCalls: "{{share}} of this tool's calls",
        total: "Failed in 12 months",
        users: "People in this period",
        tools: "Tools in this period",
        lastSeen: "Last seen",
        firstSeen: "First seen",
      },
    },
    actions: {
      label: "Actions",
      acknowledge: "Take on",
      resolve: "Resolve",
      ignore: "Ignore",
      reopen: "Reopen",
      release: "Back to open",
      note: "Add a note",
    },
    dialog: {
      acknowledge: "The issue gets the status In progress, so the admins see that Velta is on it.",
      resolve:
        "The issue gets the status Resolved. If it fails again afterwards, it comes back as Came back.",
      ignore:
        "The issue gets the status Ignored and leaves the home page, also when it fails again.",
      reopen: "The issue gets the status Open again and is back on the home page.",
      release:
        "The issue is no longer in progress and gets the status Open again, so it is back on the home page.",
      note: "The note goes in the issue's history. The status stays as it is.",
      noteLabel: "Note",
      noteOptional: "Note (optional)",
      noteHint: "Admins, owners and developers see the note, with your name.",
      noteRequired: "Type a note.",
      noteTooLong: "A note has at most {{max}} characters.",
    },
    history: {
      title: "History",
      empty: "Nobody has done anything with this issue yet.",
      byAt: "by {{who}} on {{time}}",
      done: {
        acknowledge: "Taken on",
        resolve: "Resolved",
        ignore: "Ignored",
        reopen: "Reopened",
        note: "Note",
      },
    },
  },
  speed: {
    loadError: "Failed to load the connector's speed.",
    perDay: "Per day",
    perTool: "Per tool",
    onlyRateLimited: "Only slowed down",
    odooCalls: "Odoo calls",
    noOdooCalls: "No calls to Odoo in the last {{count}} days.",
    total: "Total",
    tooltipRow: "{{label}}: {{value}}",
    charts: {
      waitShare: {
        title: "Share of time waiting",
        about:
          "The part of the Odoo calls' time they spent waiting each day for a free worker, for their turn or for a new attempt.",
      },
      waitP95: {
        title: "Wait per call",
        about: "The wait within which 95% of the Odoo calls got their turn each day (the p95).",
      },
      requestP95: {
        title: "Slowest request to Odoo",
        about:
          "The time within which Odoo answered even its slowest request for 95% of the calls each day (the p95).",
      },
      rateLimited: {
        title: "Slowed down by Odoo",
        about: "How many calls Odoo slowed down each day because too many came at once.",
      },
    },
    series: {
      worker: "Waiting for a worker",
      spacing: "Waiting for its turn",
      retry: "New attempt",
      wait: "Wait (p95)",
      request: "Slowest request (p95)",
      rateLimited: "Slowed down",
    },
    columns: {
      odooCalls: "Odoo calls",
      median: "Median",
      p95: "p95",
      wait: "Wait (p95)",
      request: "Request (p95)",
      rateLimited: "Slowed down",
    },
    hints: {
      p95: "The duration within which 95% of the tool's calls were done.",
      wait: "The wait within which 95% of the tool's Odoo calls got their turn.",
      request:
        "The time within which Odoo answered even its slowest request for 95% of the tool's calls.",
      rateLimited: "How many of the tool's calls Odoo slowed down because too many came at once.",
    },
    facts: {
      median: "median {{duration}}",
      wait: "wait {{duration}}",
      rateLimited_one: "slowed down {{count, number}} time",
      rateLimited_other: "slowed down {{count, number}} times",
    },
  },
  settings: {
    accountLoadError: "Could not load your settings.",
    roleViewNoAccount:
      "You are viewing the dashboard as a role. A role has no account of its own, so there is no name or activity to show.",
    accountInfo: "Account info",
    email: "Email",
    role: "Role",
    memberSince: "Member since",
    yourActivity: "Your activity on the dashboard",
    noActivity: "No activity yet.",
    nameTitle: "Update name",
    firstName: "First name",
    lastName: "Last name",
    nameSaved: "Profile updated.",
    aboutDescription:
      "DC Data, Claude's read-only connector to De Ceuster's Odoo, with this dashboard to manage it.",
    version: "Version",
    readMore: "Read more",
    connectClaude: "Connect Claude",
    privacy: "Privacy and data",
    roleUser: "Member",
    roleAdmin: "Admin",
    roleOwner: "Owner",
    roleDeveloper: "Developer",
  },
  viewAs: {
    description:
      "See the dashboard as someone else sees it, with their role, teams and access. While you are viewing, you cannot change anything. Every start and end goes into the activity log.",
    choice: "Whom you view",
    person: "A person",
    role: "A role",
    people: "People",
    search: "Search by name or email",
    allRoles: "All roles",
    roleCount_one: "{{count, number}} role",
    roleCount_other: "{{count, number}} roles",
    searchRole: "Search for a role",
    column: {
      name: "Name",
      role: "Role",
      teams: "Teams",
      lastActive: "Last active",
      actions: "Actions",
    },
    noCalls: "No call yet",
    lastActiveFact: "Last active on {{time}}",
    action: "View as",
    actionFor: "View as {{name}}",
    empty: "Nobody else has an account on the dashboard yet.",
    noMatch: "Nobody matches these filters.",
    loadError: "Could not load the people.",
    roles: "Roles",
    roleAccess: {
      user: "A new colleague in no team: only what everyone sees",
      admin: "Every admin page, and every tool and app through Admins",
      owner: "An admin who also sees the hours and days on the roadmap",
    },
    bar: {
      label: "View as",
      person: "You are viewing the dashboard as <strong>{{name}}</strong> ({{role}})",
      personShort: "As <strong>{{name}}</strong> ({{role}})",
      role: {
        user: "You are viewing the dashboard as a <strong>{{role}}</strong> without a team",
        admin: "You are viewing the dashboard as an <strong>{{role}}</strong>",
        owner: "You are viewing the dashboard as an <strong>{{role}}</strong>",
      },
      roleShort: {
        user: "As a <strong>{{role}}</strong> without a team",
        admin: "As an <strong>{{role}}</strong>",
        owner: "As an <strong>{{role}}</strong>",
      },
      switch: "Switch",
      readOnly: "Read-only",
      stop: "Stop",
      searchPerson: "Search for a person",
      noPeople: "Nobody found.",
    },
    toast: {
      ended: "Viewing the dashboard as someone else has ended.",
      changed: "The view was changed in another tab.",
    },
  },
  people: {
    tabs: {
      users: "People",
      teams: "Teams",
    },
  },
  users: {
    loadError: "Could not load users.",
    search: "Search by name or email",
    allRoles: "All roles",
    firstSignIn:
      "Colleagues get an account the first time they sign in with Microsoft. An admin can then change their role.",
    empty: "No users found.",
    user: "User",
    name: "Name",
    joined: "Joined",
    actions: "Actions",
    view: "View details of {{email}}",
    viewShort: "View details",
    edit: "Edit {{email}}",
    signOutEverywhereFor: "Sign {{email}} out everywhere",
    deleteFor: "Delete {{email}}",
    deleteTitle: "Delete user",
    deleteBody:
      "Are you sure you want to delete <strong>{{email}}</strong>? This action cannot be undone.",
    detailTitle: "User details",
    detailLoadError: "Failed to load user details.",
    updated: "Last updated",
    lastSignIn: "Last sign in",
    recentActivity: "Recent activity",
    editTitle: "Edit user",
    signOutEverywhere: "Sign out everywhere",
    revokeSelf:
      "You can't sign yourself out everywhere here. Use Sign out, or ask another admin to sign you out everywhere.",
    roleLockedSelf: "You can't change your own role.",
    rolesBecauseOwner: "Because you are an Owner",
    rolesBecauseDeveloper: "Because you are a Developer",
    lockedOwner: "Only an owner or a developer can change or delete an owner's account.",
    lockedDeveloper: "Only a developer can change or delete a developer's account.",
    revokeBody:
      "End every active session for <strong>{{email}}</strong>? They'll have to sign in again on every device.",
  },
  teams: {
    explainTeams:
      "Teams add up: someone in several teams gets their access combined. Anyone in no team gets the access of Everyone without a team.",
    explainExceptions:
      "An exception for one person always wins over the teams. A tool a developer pauses is off for everyone.",
    loadError: "Could not load the teams.",
    newTeam: "New team",
    defaultTeam: "Everyone without a team",
    adminsTeam: "Admins",
    adminsTeamDetail: "Admins, owners and developers, with all access",
    allAccess: "All access",
    byRole: "by role",
    byRoleAndExtra: "by role + extra",
    column: {
      team: "Team",
      members: "Members",
      access: "Access",
      actions: "Actions",
    },
    filter: {
      search: "Search by name or description",
      allAccess: "All groups and apps",
      noMatch: "No team found.",
    },
    peopleWithoutTeam_one: "{{count, number}} person without a team",
    peopleWithoutTeam_other: "{{count, number}} people without a team",
    memberCount_one: "{{count, number}} member",
    memberCount_other: "{{count, number}} members",
    noMembers: "No members yet",
    moreMembers: "+{{count, number}}",
    noAccess: "No access",
    partTitle: "{{group}}: {{access}}",
    edit: "Edit {{team}}",
    deleteFor: "Delete {{team}}",
    deleteTitle: "Delete team",
    deleteBody:
      "Are you sure you want to delete the team <strong>{{team}}</strong>? Its members keep the access of their other teams, or get that of Everyone without a team.",
    editor: {
      newTitle: "New team",
      editTitle: "Edit team",
      defaultDescription: "This team has no members.",
      name: "Team name",
      description: "Description (optional)",
      access: "Access",
      members: "Members",
      membersHint: "We recognise members by their Microsoft sign-in (email address).",
      comingSoon: "coming soon",
      removeMember: "Remove {{person}} from the team",
      removeExtra: "Remove {{person}} as an extra member",
      noMembers: "No one in this team yet.",
      adminsDescription:
        "This team has all access, also to tools and apps added later. An exception for one person still wins. This fixed team can't be renamed or deleted.",
      allAccessDetail:
        "Every group for reading and writing, every app and the tools for admins only.",
      roleMembers: "Admins, owners and developers of the dashboard",
      roleMembersHint: "They're in the team by their role. You change it on the People tab.",
      extraMembers: "Extra members",
      extraMembersHint: "For example colleagues who use Claude without a dashboard account.",
      addPeople: "Add people",
    },
    people: {
      title: "People and exceptions",
      search: "Search by name or email",
      addException: "Add an exception",
      column: {
        person: "Person",
        teams: "Teams",
        access: "Access",
        lastCall: "Last call",
      },
      allTeams: "All teams",
      teamCount_one: "{{count, number}} team",
      teamCount_other: "{{count, number}} teams",
      searchTeam: "Search for a team",
      blocked: "Blocked",
      withExceptions: "With exceptions",
      lastCallFact: "Last call {{time}}",
      noCalls: "No calls yet",
      block: "Block",
      unblock: "Unblock",
      blockFor: "Block {{person}}",
      unblockFor: "Unblock {{person}}",
      neverSignedIn: "Never signed in to Claude",
      blockedAtBy: "Blocked on {{time}} by {{email}}.",
      blockedAt: "Blocked on {{time}}.",
      blockedEffect: "DC Data refuses every call from this person until someone unblocks them.",
      empty:
        "No one known yet. People show up here once they're in a team or have used the connector.",
      noMatch: "No one found.",
      exceptions: "Exceptions",
      exceptionsFor: "Exceptions for {{person}}",
    },
    block: {
      title: "Block access to DC Data",
      unblockTitle: "Unblock access to DC Data",
      body: "Block <strong>{{who}}</strong> from DC Data? Within a minute Velta stops this person's use of the connector. Claude then gets no more tools, and every call is refused. The Microsoft account itself, managed by De Ceuster's IT partner, stays as it is.",
      unblockBody:
        "Let <strong>{{who}}</strong> use DC Data again? It takes effect within a minute, as long as this person still has an active De Ceuster Microsoft account.",
      block: "Block",
      unblock: "Unblock",
    },
    summary: {
      read: "{{groups}}: read",
      write: "{{groups}}: write",
      comingSoon: "coming soon",
      partly: "partly",
      exception: "exception",
      none: "No access",
    },
    exceptions: {
      description:
        "Allow and Deny win over the teams, and an exception for one tool wins over one for its group.",
      teams: "Teams: {{teams}}",
      choice: {
        team: "As team",
        allow: "Allow",
        deny: "Deny",
      },
      allowed: "Access",
      denied: "No access",
      partly: "Partly",
      allTools: {
        read: "All read tools",
        write: "All write tools",
      },
      clearAll: "All as team",
      choiceLabel: "Exception for {{unit}}",
    },
    apps: {
      title: "Apps",
      requires: "Requires {{units}}",
      missing: "This team still lacks {{units}}.",
      units: {
        read: "{{groups}}: read",
        write: "{{groups}}: write",
      },
      groups: {
        account: "Your account",
        clients: "Clients",
        cranes: "Cranes",
        orders: "Orders",
        finance: "Finance",
        system: "System",
      },
      grant: "App {{app}}",
      summary: "app {{apps}}",
      exceptionMissing: "Lacks {{units}}",
    },
  },
  picker: {
    search: "Search by name or email",
    empty: "No one found.",
    addByEmail: "Add by email",
    invalidEmail: "Type a full email address to add someone.",
    domainNotAllowed: "Only addresses at {{domains}} can get access.",
  },
  roadmap: {
    newIdea: "New idea",
    proposeIdea: "Propose an idea",
    loadError: "Could not load the roadmap.",
    empty: "Nothing on the roadmap yet. Add a first idea.",
    emptyPropose: "Nothing on the roadmap yet. Propose a first idea.",
    status: {
      idea: "Idea",
      planned: "Planned",
      in_progress: "In progress",
      done: "Done",
    },
    priority: {
      high: "High",
      medium: "Medium",
      low: "Low",
    },
    priorityLong: {
      high: "High priority",
      medium: "Medium priority",
      low: "Low priority",
    },
    facts: {
      deadline: "deadline {{date}}",
    },
    groups: {
      done: { title: "Done" },
      in_progress: { title: "In progress" },
      planned: { title: "Planned" },
      idea: { title: "Ideas" },
    },
    itemCount_one: "{{count, number}} item",
    itemCount_other: "{{count, number}} items",
    groupTotal: "{{items}}, {{effort}}",
    column: {
      item: "Item",
      priority: "Priority",
      effort: "Effort",
      month: "Month",
      deadline: "Deadline",
      followers: "Followers",
    },
    sort: {
      hint: {
        item: "Sort by title from A to Z. Click again for Z to A, and a third time for the default order.",
        priority:
          "Sort by priority from low to high. Click again for high to low, and a third time for the default order.",
        effort:
          "Sort by effort from small to large. Click again for large to small, and a third time for the default order.",
        month:
          "Sort by month from earliest to latest. Click again for latest to earliest, and a third time for the default order.",
        deadline:
          "Sort by deadline from earliest to latest. Click again for latest to earliest, and a third time for the default order.",
        followers:
          "Sort by number of followers from fewest to most. Click again for most to fewest, and a third time for the default order.",
      },
      by: {
        item: "title",
        priority: "priority",
        effort: "effort",
        month: "month",
        deadline: "deadline",
        followers: "number of followers",
      },
      asc: {
        item: "from A to Z",
        priority: "from low to high",
        effort: "from small to large",
        month: "from earliest to latest",
        deadline: "from earliest to latest",
        followers: "from fewest to most",
      },
      desc: {
        item: "from Z to A",
        priority: "from high to low",
        effort: "from large to small",
        month: "from latest to earliest",
        deadline: "from latest to earliest",
        followers: "from most to fewest",
      },
      active: "Each group is sorted by {{column}}, {{order}}.",
      reset: "Default order",
    },
    filter: {
      search: "Search by title or summary",
      searchDeveloper: "Search by title, summary or Linear id",
      priority: "Priority",
      allPriorities: "All priorities",
      toggles: "Quick filters",
      blocked: "Blocked",
      waitingOnMe: "Waiting on me",
      followedByMe: "Followed by me",
      empty: "No items found.",
    },
    effort: {
      hours: "{{hours}} h",
      day: "{{days}} day",
      days: "{{days}} days",
      unknown: "To be estimated",
      estimated: "estimated",
      actual: "actual",
      hoursAndDays: "{{hours}} ({{days}})",
      less: "Less effort",
      more: "More effort",
    },
    blockers: {
      symbol: "Blockage",
      separator: ": ",
      itemStatus: "({{status}})",
      more_one: "+{{count, number}} other blockage",
      more_other: "+{{count, number}} other blockages",
      nothing: "Waiting on nothing and no one",
      since: "since {{date}}",
      resolvedBy: "resolved by {{person}} on {{date}}",
      resolvedOn: "resolved on {{date}}",
      resolve: "Resolved",
      resolveLabel: "Mark the blockage of {{who}} as resolved",
      remove: "Remove the blockage of {{who}}",
      add: "Blockage",
      addTitle: "Add a blockage",
      who: "Who",
      kinds: {
        people: "People",
        item: "Item",
        party: "Other",
      },
      choosePeople: "Choose people",
      chooseItem: "Choose an item",
      other: "What it waits on",
      otherPlaceholder: "For example a supplier",
      otherTooLong: "The text under Other takes at most {{max}} characters. Make it shorter.",
      ask: "What",
      askCount: "{{length}}/{{max}}",
      askCountLabel: "{{length}} of {{max}} characters",
      askTooLong: "“What” takes at most {{max}} characters. Make it shorter.",
      askOptional: "What (optional)",
      askHint: "Write it so that it reads after the name, for example “send the price list”.",
      create: "Add",
      card: "Blockages",
      cardNone: "none",
      cardOpen_one: "{{count, number}} open",
      cardOpen_other: "{{count, number}} open",
      removeMode: "Remove blockages",
      removeDone: "Done removing",
      resolvedCount_one: "{{count, number}} resolved",
      resolvedCount_other: "{{count, number}} resolved",
    },
    followers: {
      unfollow: "Unfollow",
      followedBy: "Followed by {{names}}",
      follow: "Follow",
      followItem: "Follow {{item}}",
      more: "+{{count, number}}",
      person: "{{name}}, {{role}}",
      none: "No followers yet",
      closed: "This item was finished in the meantime, so its followers are fixed.",
    },
    month: {
      budgetHint: "In whole hours, a day is 8 hours.",
      budgetInvalid: "Enter a whole number of hours, 0 or more.",
      previous: "Previous month",
      next: "Next month",
      budget: "Budget",
      done: "Done",
      planned: "Still planned",
      left: "Left",
      over: "Over budget",
      editBudget: "Change budget",
      budgetHours: "Budget for {{month}} in hours",
      legendDone: "Done",
      legendPlanned: "Planned or in progress",
      legendOver: "Over budget",
      budgetLine: "Budget {{effort}}",
      chart:
        "In {{month}}, {{done}} of work is done and {{planned}} of work is still planned, against a budget of {{budget}}.",
      chartProgress: "In {{month}}, {{done}} and {{planned}}.",
      chartDone_one: "{{count, number}} item is done",
      chartDone_other: "{{count, number}} items are done",
      chartPlanned_one: "{{count, number}} item is planned or in progress",
      chartPlanned_other: "{{count, number}} items are planned or in progress",
      segment: "{{title}}, {{hours}}",
      empty: "Nothing planned in {{month}} yet.",
      emptyPast: "Nothing was done in {{month}}.",
      confirmBody: "The budget for {{month}} goes from {{from}} to {{to}}.",
      confirmNotify: "The development team will be notified.",
      confirm: "Confirm",
      budgetForbidden: "Your role doesn't let you change this month's budget.",
    },
    details: {
      noSummary: "No description.",
      summary: "Description",
      editContent: "Edit the title, description and priority",
      editText: "Edit the title and description",
      editPriority: "Change the priority",
      editingContent: "Editing the title, description and priority",
      editingText: "Editing the title and description",
      editingPriority: "Changing the priority",
      stage: "Lifecycle",
      passed: "(passed)",
      ideaSince: "since {{date}}",
      planIn: "Plan in",
      planOther: "Another month",
      effortByVelta: "Velta chooses an effort first.",
      doneOn: "Done on {{date}}",
      waitsStart: "Velta sets it to In progress once the work starts.",
      waitsFinish: "Velta marks it done once it is finished.",
      openBlockers_one: "Still waiting on {{count, number}} blockage",
      openBlockers_other: "Still waiting on {{count, number}} blockages",
      planning: "Planning",
      editPlanning: "Edit the planning",
      notPlanned: "Not planned yet",
      monthAtPlan: "You choose it when planning.",
      people: "People",
      effort: "Effort",
      month: "Month",
      deadline: "Deadline",
      none: "None",
      addedBy: "Added by {{person}} on {{date}}",
      lockedInProgress:
        "This item is in progress, so its status, title, description, deadline and month are fixed.",
      lockedDone: "This item is done and fixed.",
      lockedPast: "This is a past month, so this item can't be changed here.",
      unplan: "Back to ideas",
      start: "Start",
      unstart: "Back to planned",
      finish: "Mark done",
      delete: "Delete",
      finishActual: "Actual hours (optional)",
      finishHint: "Leave it empty to count the estimate.",
      deleteBody: "Do you really want to delete <strong>{{item}}</strong>? This can't be undone.",
      openItem: "Open {{item}}",
    },
    statusMenu: {
      plan: "Plan in {{month}}",
      finish: "Mark done…",
      needsEffort: "Choose an effort first",
      trigger: "{{status}}. Change the status of {{item}}",
      tooltip: "{{status}}. Click to change the status.",
    },
    form: {
      noDeadline: "No deadline",
      newTitle: "New idea",
      newDescription: "A new idea goes straight under Ideas, with the Linear tickets behind it.",
      proposeDescription: "Velta reviews every proposal and puts it on the roadmap where it fits.",
      title: "Title",
      titleRequired: "Give the item a title.",
      summary: "Summary",
      priority: "Priority",
      priorityOptional: "Priority (optional)",
      noPriority: "No preference",
      language: "Language",
      languageHint: "The language the title and the summary are written in.",
      linear: "Linear tickets",
      linearPlaceholder: "ABC-12, ABC-13",
      linearHint: "One or more, separated by commas or spaces, such as ABC-12, ABC-13.",
      linearRequired: "Give at least one Linear id, such as ABC-12.",
      linearInvalid_one: "{{ids}} is not a Linear id. A Linear id looks like ABC-12.",
      linearInvalid_other: "{{ids}} are not Linear ids. A Linear id looks like ABC-12.",
      linearTooMany: "An item has at most {{max}} Linear tickets.",
      effortOptional: "Effort (optional)",
      propose: "Propose",
      proposed: "Thank you. Velta will review your idea and put it on the roadmap where it fits.",
      itemSearch: "Search for an item",
      noItems: "No items found.",
      remove: "Remove {{name}}",
      create: "Add idea",
    },
    developer: {
      noLinear: "No tickets yet.",
      estimateRequired: "A planned item keeps an estimate.",
      actualInvalid: "Actual hours are a whole number of hours, 0 or more.",
      title: "Developer",
      edit: "Edit the developer's part",
      actual: "Actual hours",
      actualMissing: "Not filled in, the estimate counts.",
      actualAtFinish: "You fill them in at Mark done.",
      hoursUnit: "h",
      linear: "Linear tickets",
      linearAdd: "Add the Linear id",
      linearInput: "Linear id, such as ABC-123",
      linearInvalid: "A Linear id looks like ABC-123.",
      linearRemove: "Remove {{id}}",
      linearOpen: "{{id}} in Linear (opens in a new tab)",
      commandCopy: "Copy the command",
      commandCopied: "Copied",
      commandCopiedStatus: "The command is copied.",
      commandCopyFailed: "Copying did not work. Select the command and copy it yourself.",
    },
    comments: {
      card: "Comments",
      none: "No comments yet.",
      loadError: "Could not load the comments.",
      input: "New comment",
      placeholder: "Write a comment. Type @ to mention someone.",
      post: "Post",
      mentionList: "Mention someone",
      delete: "Delete {{person}}'s comment",
      deleteBody: "Do you really want to delete this comment? This cannot be undone.",
      closed: "This item is done, so it takes no more comments.",
      count_one: "{{count, number}} comment",
      count_other: "{{count, number}} comments",
    },
    triage: {
      title: "Triage",
      kinds: {
        idea: "Idea",
        question: "Question",
      },
      submittedBy: "Submitted by {{person}} on {{date}}",
      reasons: {
        missing_tool: "Missing tool",
        missing_rights: "Missing rights",
        odoo_data_missing: "Missing in Odoo",
        access_withheld: "Access withheld",
        other: "Other",
      },
      showDetails: "Details",
      body: {
        idea: "Summary",
        question: "Question",
      },
      toolsTried: "Tools tried",
      noTools: "None",
      detail: "Claude's note",
      actions: {
        idea: "Actions for {{person}}'s idea",
        question: "Actions for {{person}}'s question",
      },
      accept: "Add to backlog",
      acceptLabel: {
        idea: "Add {{person}}'s idea to the backlog",
        question: "Add {{person}}'s question to the backlog",
      },
      close: "Close",
      closeLabel: {
        idea: "Close {{person}}'s idea",
        question: "Close {{person}}'s question",
      },
      acceptDescription: {
        idea: "The item goes under Ideas and leaves Triage.",
        question:
          "The item goes under Ideas and the question leaves Triage. Write the title and the summary in your own words and leave out any client details. The question itself does not go on the roadmap.",
      },
      closeTitle: {
        idea: "Close idea",
        question: "Close question",
      },
      closeDescription: {
        idea: "The idea leaves Triage without going on the roadmap.",
        question: "The question leaves Triage without going on the roadmap.",
      },
      closeNote: "Note (optional)",
      closeNoteHint:
        "Why you close it, for example “already answered” or “not a fit for DC Data”. Whoever sent it in reads this note in Claude.",
      merge: "Merge into…",
      mergeLabel: {
        idea: "Merge {{person}}'s idea into an item",
        question: "Merge {{person}}'s question into an item",
      },
      mergeTitle: {
        idea: "Merge idea",
        question: "Merge question",
      },
      mergeDescription: {
        idea: "The idea leaves Triage and from then on belongs to an item already on the roadmap. Whoever submitted it follows that item if they can see the roadmap.",
        question:
          "The question leaves Triage and from then on belongs to an item already on the roadmap. The question itself does not go on the roadmap.",
      },
      mergeItem: "Item",
      mergeItemHint: "Every item that is not done yet.",
      mergeChoose: "Choose an item",
      mergeConfirm: "Merge",
      mergeItemDone: "This item is done by now. Choose another item.",
      mergeItemGone: "This item is no longer on the roadmap. Choose another item.",
      delete: "Delete",
      deleteLabel: {
        idea: "Delete {{person}}'s idea",
        question: "Delete {{person}}'s question",
      },
      deleteTitle: {
        idea: "Delete idea",
        question: "Delete question",
      },
      deleteDescription: {
        idea: "The idea is deleted for good, including the text {{person}} submitted. This cannot be undone. Close takes it out of Triage without deleting it.",
        question:
          "The question is deleted for good, including the text {{person}} submitted. This cannot be undone. Close takes it out of Triage without deleting it.",
      },
    },
  },
  activity: {
    loadError: "Could not load activity.",
    events: "Events",
    logins: "Logins",
    overTime: "Activity over time",
    allEvents: "All events",
    byAction: "By action",
    recent: "Recent activity",
    search: "Search activity…",
    people: "People",
    allPeople: "Everyone",
    searchPerson: "Search for a person",
    actionsFilter: "Actions",
    allActions: "All actions",
    searchAction: "Search for an action",
    previous: "Prev",
    next: "Next",
    page: "Page {{page, number}} of {{total, number}}",
    action: "Action",
    when: "When",
    empty: "No activity found.",
    dayTooltip: "{{day}}: {{events}}, {{logins}}",
    eventCount_one: "{{count, number}} event",
    eventCount_other: "{{count, number}} events",
    loginCount_one: "{{count, number}} login",
    loginCount_other: "{{count, number}} logins",
    actions: {
      login: "Signed in",
      signup: "Signed up",
      profile_update: "Name changed",
      password_change: "Password changed",
      admin_update_user: "User edited",
      admin_delete_user: "User deleted",
      admin_revoke_sessions: "Signed out everywhere",
      admin_invite: "User invited",
      admin_first_account: "First account made admin",
      admin_mcp_enable_tool: "Tool switched on",
      admin_mcp_disable_tool: "Tool switched off",
      admin_mcp_enable_tool_group: "Tool group switched on",
      admin_mcp_disable_tool_group: "Tool group switched off",
      admin_mcp_block_user: "Connector user blocked",
      admin_mcp_unblock_user: "Connector user unblocked",
      admin_mcp_team_create: "Team created",
      admin_mcp_team_update: "Team changed",
      admin_mcp_team_delete: "Team deleted",
      admin_mcp_team_members: "Team members changed",
      admin_mcp_person_override: "Exception changed",
      admin_mcp_issue_acknowledge: "Issue taken on",
      admin_mcp_issue_resolve: "Issue resolved",
      admin_mcp_issue_ignore: "Issue ignored",
      admin_mcp_issue_reopen: "Issue reopened",
      roadmap_item_create: "Roadmap item added",
      roadmap_item_update: "Roadmap item changed",
      roadmap_item_status: "Roadmap item status changed",
      roadmap_item_delete: "Roadmap item deleted",
      roadmap_item_effort: "Roadmap item effort changed",
      roadmap_month_budget: "Monthly budget changed",
      roadmap_item_blocker: "Roadmap item blockage changed",
      roadmap_item_comment: "Roadmap item comment added or deleted",
      roadmap_gap_promote: "Question turned into a roadmap idea",
      roadmap_gap_close: "Question marked as answered",
      roadmap_triage_propose: "Roadmap idea proposed",
      roadmap_triage_accept: "Triage idea or question added to the roadmap",
      roadmap_triage_close: "Triage idea or question closed or deleted",
      view_as_start: "Started viewing as",
      view_as_switch: "Switched viewing as",
      view_as_stop: "Stopped viewing as",
    },
  },
  apps: {
    bezetting: {
      title: "Occupancy",
      summary:
        "How much of the fleet is rented today, what is booked and how it evolves, by crane, area and client.",
    },
    maandrapport: {
      title: "Monthly report",
      summary:
        "Revenue, clients, payments and prices month by month, against the same period last year.",
      emptyBody:
        "Recalculate reads the data in Odoo and works out the monthly report from it. It takes a few minutes, and you can keep working in the meantime.",
      outdatedTitle: "The monthly report is being updated",
      outdatedBody:
        "This data is being recalculated because the monthly report has a new version. That takes a few minutes. If no recalculation is running, choose Recalculate.",
      jobErrors: {
        odoo_data_missing:
          "Recalculating failed because Odoo returned no invoices, orders, payments or list prices.",
      },
    },
    unread_one: "{{count, number}} unread alert",
    unread_other: "{{count, number}} unread alerts",
    linkWithUnread: "{{app}}, {{unread}}",
    loadError: "{{app}} could not be loaded.",
    meldingenError: "The alerts could not be loaded, so this page does not show them.",
    pageError: "Part of this page could not be shown. Reload the page to try again.",
    previousKept: "You are still seeing the previous data.",
    denied: {
      title: "No access",
      contactsTitle: "Who grants access",
      noContacts: "Ask an admin of the dashboard.",
    },
    empty: {
      title: "No data yet",
      body: "Recalculate reads the data in Odoo and works out the occupancy from it. It takes a few minutes, and you can keep working in the meantime.",
    },
    recompute: {
      start: "Recalculate",
      running: "Recalculating…",
      waiting: "You can recalculate again at {{time}}",
      unavailable: "Recalculating isn't possible while Odoo is not linked",
      runningPercent: "Recalculating, {{percent}}",
    },
    progress: {
      title: "Recalculate",
      lead: "The current figures stay visible until the new ones are ready.",
      leadFirst: "The figures appear on this page as soon as they are ready.",
      label: "Progress of the recalculation",
      running: "Running for {{time}}",
      finished: "Done after {{time}}",
      stopped: "Stopped after {{time}}",
      done: "Done. The page now shows the Odoo data as of {{time}}.",
      notLoaded: "The new figures have been calculated, but they could not be loaded yet.",
      hide: "Hide",
      steps: {
        odoo_read: "Reading Odoo",
        check: "Checking the data",
        build: "Calculating the pages",
        store: "Saving",
      },
      status: {
        pending: "not started yet",
        active: "in progress",
        done: "done",
        failed: "failed",
      },
      read_one: "{{table}}, {{done, number}} of {{count, number}} record",
      read_other: "{{table}}, {{done, number}} of {{count, number}} records",
      readCount_one: "{{table}}, {{count, number}} record",
      readCount_other: "{{table}}, {{count, number}} records",
      tables: {
        sale_order: "Orders",
        sale_order_line: "Order lines",
        account_move: "Invoices",
        account_move_line: "Invoice lines",
        res_partner: "Clients and contacts",
        fleet_vehicle: "Cranes",
        product_product: "Products",
        res_company: "Companies",
        sale_order_log: "Order history",
        account_partial_reconcile: "Payment matching",
        product_pricing: "Rental prices",
        receivable_line: "Open items",
        stock_picking: "Deliveries",
        sale_pricing_crane_transport: "Transport rates",
        fleet_vehicle_model: "Crane types",
        account_analytic_account: "Analytic accounts",
      },
      parts: {
        fleet: "Cranes and rentals",
        geo: "Service area",
        places: "Sites and places",
        pages: "Assembling the pages",
        crane_tables: "Crane tables",
        finish: "Finishing and checking",
        omzet: "Revenue",
        klanten: "Clients",
        betalingen: "Invoices and payments",
        prijzen: "Prices",
        ongefactureerd: "Unbilled work",
      },
    },
    jobErrors: {
      odoo_not_configured: "Recalculating failed because Odoo is not linked.",
      odoo_unavailable:
        "Recalculating failed because Odoo did not answer. Try again in a few minutes.",
      odoo_access_refused:
        "Recalculating failed because the Odoo account may not read all the data it needs.",
      odoo_field_missing: "Recalculating failed because a field in Odoo is missing or has changed.",
      odoo_time_budget: "Recalculating failed because reading Odoo took too long. Try again later.",
      odoo_data_missing:
        "Recalculating failed because Odoo returned far fewer cranes than usual, or no rentals.",
      compute_failed: "Recalculating failed because the calculation broke off.",
      snapshot_invalid: "Recalculating failed because the result did not add up. It was not saved.",
      store_failed: "Recalculating failed because the result could not be saved.",
      abandoned: "Recalculating was stopped because it took too long.",
      interrupted: "Recalculating was interrupted because the server restarted.",
      unknown: "Recalculating failed.",
    },
  },
  login: {
    title: "Sign in to {{app}}",
    microsoft: "Sign in with Microsoft",
    hint: "Sign in with your De Ceuster Microsoft account.",
    microsoftErrors: {
      microsoft_cancelled:
        "Microsoft sign-in was cancelled, or your account has no access to this app.",
      microsoft_failed: "Microsoft sign-in failed. Please try again.",
      email_not_verified:
        "Your Microsoft account has no verified email address. Ask your IT administrator.",
      domain_not_allowed: "This email domain isn't allowed to sign in.",
      no_account: "No account was found for this Microsoft user.",
    },
  },
  errors: {
    codes: {
      not_authenticated: "You are not signed in. Please sign in again.",
      invalid_session: "Your session has expired. Please sign in again.",
      session_revoked: "Your session has ended. Please sign in again.",
      account_not_found: "No account was found. Ask an admin for access.",
      admin_required: "Only an admin can do this.",
      owner_required: "Only an owner can do this.",
      developer_required: "Only a developer can do this.",
      view_as_read_only:
        "You are viewing the dashboard as someone else. While you are viewing, you cannot change anything.",
      view_as_self: "You cannot view the dashboard as yourself.",
      view_as_ended:
        "You are no longer viewing the dashboard as someone else. The page is reloading.",
      auth_unavailable: "Your account could not be checked just now. Try again in a minute.",
      revoke_failed: "The sessions could not be ended. Nothing was changed.",
      user_not_found: "This user no longer exists.",
      invalid_role: "This role does not exist.",
      cannot_demote_self: "You can't change your own role.",
      cannot_delete_self: "You can't delete your own account.",
      cannot_revoke_self: "You can't sign yourself out everywhere here.",
      role_assignment_forbidden: "Your role doesn't let you give this role.",
      user_change_forbidden: "Your role doesn't let you change or delete this account.",
      tool_not_found: "This tool no longer exists.",
      tool_not_switchable: "This tool can't be switched off.",
      team_not_found: "This team no longer exists.",
      team_name_taken: "There's already a team with this name.",
      team_not_deletable: "Everyone without a team can't be deleted.",
      team_not_editable: "For Everyone without a team, you can only change the access.",
      team_member_not_allowed: "Only De Ceuster email addresses can join a team.",
      person_not_allowed: "Exceptions are only possible for De Ceuster email addresses.",
      unit_unknown: "One of these tools or groups no longer exists. Reload the page.",
      service_unavailable: "The service can't be reached right now. Try again in a minute.",
      app_access_required: "You don't have access to this app. Ask an admin.",
      app_not_for_default_team: "Everyone without a team can't get an app. Give the app to a team.",
      admins_team_not_deletable: "Admins is a fixed team and can't be deleted.",
      admins_team_not_editable: "For Admins, you can only change the extra members.",
      snapshot_not_found: "This data has been replaced in the meantime. Reload the page.",
      section_not_found: "This part of the data doesn't exist.",
      job_not_found: "This recalculation no longer exists.",
      recompute_running: "A recalculation is already running.",
      recompute_too_soon: "The data was just recalculated. Try again in a few minutes.",
      odoo_not_configured: "Odoo is not linked, so recalculating isn't possible.",
      snapshot_outdated:
        "The monthly report is being recalculated for the new version. That takes a few minutes.",
      invalid_range: "This period can't be shown. Choose another period.",
      detail_not_found: "There is no data for this choice.",
      client_not_found: "There is no data about this client.",
      roadmap_item_not_found: "This item is no longer on the roadmap.",
      roadmap_forbidden: "Your role doesn't let you change this item that way.",
      roadmap_transition_not_allowed:
        "This item can't move to that status. Reload the page to see where it stands.",
      roadmap_estimate_required: "This item needs an estimate before it can be planned.",
      roadmap_waits_loop: "This item can't wait on an item that itself waits on this one.",
      roadmap_unknown_person: "One of these people has no account on the dashboard.",
      roadmap_linear_id_taken: "This Linear ticket already belongs to another item.",
      roadmap_effort_not_a_step: "Choose an effort from the list.",
      roadmap_blocker_not_found: "This blockage is no longer on this item.",
      roadmap_blocker_invalid:
        "Choose who or what holds the item up, and say what needs to happen.",
      roadmap_item_done: "This item is done, so it takes no more comments.",
      roadmap_mention_invalid:
        "You can only mention the dashboard's admins, owners and developers.",
      roadmap_comment_not_found: "This comment no longer exists.",
      roadmap_triage_not_found: "This idea or question is no longer in Triage.",
      roadmap_triage_closed:
        "This idea or question has already been added to the roadmap or closed.",
      tool_issue_not_found: "This issue does not exist, or its failed calls are no longer kept.",
    },
    fallback: {
      loadToolDefinition: "Could not load the description for Claude.",
      updateProfile: "Failed to update profile.",
      updateUser: "Failed to update user.",
      deleteUser: "Failed to delete user.",
      revokeSessions: "Failed to sign the user out.",
      blockUser: "Failed to block this person.",
      unblockUser: "Failed to unblock this person.",
      issueAction: "Failed to update this issue.",
      switchToolOn: "Could not switch this tool on. Try again in a minute.",
      switchToolOff: "Could not switch this tool off. Try again in a minute.",
      saveTeam: "Could not save the team.",
      deleteTeam: "Could not delete the team.",
      saveExceptions: "Could not save the exceptions.",
      recompute: "Could not start recalculating. Try again in a minute.",
      saveMeldingen: "Could not save the alert. Try again.",
      appData: "Could not load the data. Reload the page.",
      createRoadmapItem: "Could not add the idea.",
      updateRoadmapItem: "Could not save the item.",
      setRoadmapStatus: "Could not change the status.",
      setRoadmapEffort: "Could not save the effort.",
      followRoadmapItem: "Could not change whether you follow this item. Try again.",
      deleteRoadmapItem: "Could not delete the item.",
      setRoadmapBudget: "Could not save the budget.",
      addRoadmapBlocker: "Could not add the blockage.",
      resolveRoadmapBlocker: "Could not mark the blockage as resolved.",
      deleteRoadmapBlocker: "Could not remove the blockage.",
      addRoadmapComment: "Could not post the comment.",
      deleteRoadmapComment: "Could not delete the comment.",
      proposeRoadmapIdea: "Could not propose the idea.",
      acceptRoadmapTriage: "Could not add the item to the backlog.",
      closeRoadmapTriage: "Could not close the item.",
      mergeRoadmapTriage: "Could not merge the item.",
      deleteRoadmapTriage: "Could not delete the item.",
      startViewAs: "Could not start viewing as. Try again.",
      stopViewAs: "Could not stop viewing as. Try again.",
    },
  },
};
